
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface NotificationContextType {
  unreadCount: number;
  notifications: any[];
  requestNotificationPermission: () => Promise<void>;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasPermission, setHasPermission] = useState(false);

  // Request notification permission
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setHasPermission(permission === 'granted');
    }
  }, []);

  // Show browser notification
  const showBrowserNotification = useCallback((title: string, body: string) => {
    if (hasPermission && document.hidden) {
      new Notification(title, {
        body,
        icon: '/favicon.ico',
        tag: 'uplivion-notification'
      });
    }
  }, [hasPermission]);

  // Update browser tab title with unread count
  const updateTabTitle = useCallback((count: number) => {
    const baseTitle = 'Uplivion';
    document.title = count > 0 ? `(${count}) ${baseTitle}` : baseTitle;
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setNotifications(data || []);
      const unread = (data || []).filter(n => !n.is_read).length;
      setUnreadCount(unread);
      updateTabTitle(unread);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [user?.id, updateTabTitle]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      if (error) throw error;
      await fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, [fetchNotifications]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) throw error;
      await fetchNotifications();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, [user?.id, fetchNotifications]);

  // Handle new notifications
  const handleNewNotification = useCallback((payload: any) => {
    const notification = payload.new;
    
    // Show toast notification
    let message = '';
    switch (notification.type) {
      case 'friend_request':
        message = 'Du hast eine neue Freundschaftsanfrage erhalten';
        break;
      case 'new_entry':
        message = 'Jemand hat eine neue Erinnerung zu deiner Timeline hinzugefügt';
        break;
      case 'entry_approved':
        message = 'Deine Timeline-Erinnerung wurde genehmigt';
        break;
      default:
        message = notification.message || 'Neue Benachrichtigung';
    }

    toast.success(message);
    showBrowserNotification('Uplivion', message);
    
    // Refresh notifications
    fetchNotifications();
  }, [fetchNotifications, showBrowserNotification]);

  // Set up initial data fetch and permissions (realtime handled by OptimizedRealtimeProvider)
  useEffect(() => {
    if (!user?.id) return;

    // Initial fetch
    fetchNotifications();

    // Request notification permission on first load
    requestNotificationPermission();

    // Note: Real-time subscriptions are now handled by OptimizedRealtimeProvider
    // This prevents duplicate subscriptions and reduces query overhead
  }, [user?.id, fetchNotifications, requestNotificationPermission]);

  // Clean up tab title when component unmounts
  useEffect(() => {
    return () => {
      document.title = 'Uplivion';
    };
  }, []);

  const value = {
    unreadCount,
    notifications,
    requestNotificationPermission,
    markAsRead,
    markAllAsRead
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
